import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['email', 'submit', 'phonePrefix', 'countryDropdown', 'flagDisplay', 'codeDisplay', 'chevron', 'validationError', 'phoneCode']
  static values = { minDigits: Number, maxDigits: Number }

  connect() {
    this.selectedCode = '+65'
    this.isPhoneMode = false
    this.boundCloseDropdown = this.closeDropdown.bind(this)
    this.boundHandleSubmit = this.handleSubmit.bind(this)
    this.element.addEventListener('submit', this.boundHandleSubmit)
    this.updateState()
  }

  disconnect() {
    document.removeEventListener('click', this.boundCloseDropdown)
    this.element.removeEventListener('submit', this.boundHandleSubmit)
  }

  handleInput() {
    this.clearValidationError()
    this.updateState()
  }

  handleSubmit(e) {
    const value = this.emailTarget.value.trim()
    const error = this.validate(value)

    if (error) {
      e.preventDefault()
      this.showValidationError(error)
      return
    }

    this.clearValidationError()

    if (this.isPhoneMode && this.hasPhoneCodeTarget) {
      this.phoneCodeTarget.value = this.selectedCode
    }
  }

  validate(value) {
    if (value.length === 0) {
      return 'Please enter your email or phone number.'
    }

    if (this.isPhoneMode) {
      const digits = value.replace(/\s/g, '')
      if (digits.length < this.minDigitsValue || digits.length > this.maxDigitsValue) {
        return 'Please enter a valid phone number.'
      }
    } else {
      const emailRegex = /^[^\s@]+@[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z]{2,})+$/
      if (!emailRegex.test(value)) {
        return 'Please enter a valid email address.'
      }
    }
    return null
  }

  showValidationError(message) {
    if (this.hasValidationErrorTarget) {
      this.validationErrorTarget.textContent = message
      this.validationErrorTarget.classList.remove('hidden')
    }
    this.emailTarget.setAttribute('aria-invalid', 'true')
    // The label sits outside the input, so it can't key off aria-invalid.
    // Flag the form instead and let group-data-[error] colour it red.
    this.element.dataset.error = ''
  }

  clearValidationError() {
    if (this.hasValidationErrorTarget) {
      this.validationErrorTarget.textContent = ''
      this.validationErrorTarget.classList.add('hidden')
    }
    this.emailTarget.removeAttribute('aria-invalid')
    delete this.element.dataset.error
  }

  updateState() {
    const value = this.emailTarget.value.trim()
    this.submitTarget.disabled = value.length === 0
    this.togglePhonePrefix(value)
  }

  togglePhonePrefix(value) {
    if (!this.hasPhonePrefixTarget) return

    const bareDigits = value.length > 0 && /^\d[\d\s]*$/.test(value)
    this.isPhoneMode = bareDigits

    this.element.dataset.mode = bareDigits ? 'phone' : 'email'
    if (bareDigits) {
      this.element.style.setProperty('--prefix-width', `${this.phonePrefixTarget.offsetWidth + 8}px`)
    } else {
      this.element.style.removeProperty('--prefix-width')
    }
    this.emailTarget.setAttribute('inputmode', bareDigits || value.startsWith('+') ? 'numeric' : 'email')

    if (!bareDigits) this.closeDropdown()
  }

  toggleCountryPicker(e) {
    e.preventDefault()
    e.stopPropagation()

    if (this.countryDropdownTarget.hasAttribute('data-open')) {
      this.closeDropdown()
    } else {
      this.openDropdown()
    }
  }

  openDropdown() {
    this.countryDropdownTarget.dataset.open = ''
    this.chevronTarget.classList.add('rotate-180')
    document.addEventListener('click', this.boundCloseDropdown)
  }

  closeDropdown() {
    if (!this.hasCountryDropdownTarget) return
    delete this.countryDropdownTarget.dataset.open
    if (this.hasChevronTarget) this.chevronTarget.classList.remove('rotate-180')
    document.removeEventListener('click', this.boundCloseDropdown)
  }

  selectCountry(e) {
    e.preventDefault()
    e.stopPropagation()

    const { code, flag, minDigits, maxDigits } = e.currentTarget.dataset
    this.selectedCode = code
    this.minDigitsValue = parseInt(minDigits, 10)
    this.maxDigitsValue = parseInt(maxDigits, 10)
    this.flagDisplayTarget.textContent = flag
    this.codeDisplayTarget.textContent = code

    this.clearValidationError()
    this.closeDropdown()
    this.emailTarget.focus()
  }
}
